import { NetworkStatus } from '@apollo/client';
import type { QueryRef } from '@apollo/client/react';
import { useQueryRefHandlers, useReadQuery } from '@apollo/client/react';
import { startTransition, useCallback } from 'react';
import { useLoaderData } from 'react-router';

import { useLoadMore } from './usePaginatedQuery.ts';

export const useQueryLoader = <TData>() => {
  const queryRef = useLoaderData() as QueryRef<TData>;
  if (!queryRef) {
    throw new Error(
      'useQueryLoader must be used within a route that has a query loader. Did you forget to export the loader?'
    );
  }
  const { refetch, fetchMore, subscribeToMore } = useQueryRefHandlers(queryRef);
  const { data, error, networkStatus } = useReadQuery(queryRef);
  const loadMore = useLoadMore({ fetchMore, options: null });

  // Transition keeps the stale UI painted instead of unmounting to the Suspense
  // fallback while the queryRef re-suspends; the promise is preserved for callers.
  const refetchInTransition = useCallback(
    async (...args: Parameters<typeof refetch>) => {
      let promise!: ReturnType<typeof refetch>;
      startTransition(() => {
        promise = refetch(...args);
      });
      return promise;
    },
    [refetch]
  );

  return {
    data,
    error,
    networkStatus,
    refetch: refetchInTransition,
    loadMore: (...args: Parameters<typeof loadMore>) =>
      startTransition(() => {
        loadMore(...args);
      }),
    subscribeToMore,
    loading: networkStatus === NetworkStatus.loading,
  };
};
